import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
         XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

const s = {
  title:   { fontSize:22, fontWeight:600, color:'#f1f5f9', marginBottom:6 },
  sub:     { fontSize:13, color:'#64748b', marginBottom:28 },
  filter:  { display:'flex', gap:8, marginBottom:24 },
  fBtn:    (active) => ({
             background: active ? '#0ea5e9' : '#1e293b',
             color: active ? '#fff' : '#94a3b8',
             border:'1px solid #334155', borderRadius:8,
             padding:'7px 18px', cursor:'pointer', fontSize:13
           }),
  grid:    { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:28 },
  kpi:     { background:'#1e293b', border:'1px solid #334155', borderRadius:12,
             padding:'20px 22px' },
  kpiVal:  { fontSize:32, fontWeight:700, color:'#38bdf8' },
  kpiLbl:  { fontSize:12, color:'#64748b', marginTop:4 },
  kpiSub:  { fontSize:11, color:'#22c55e', marginTop:2 },
  row:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 },
  card:    { background:'#1e293b', border:'1px solid #334155', borderRadius:12, padding:20 },
  cardTit: { fontSize:14, fontWeight:600, color:'#94a3b8', marginBottom:16,
             textTransform:'uppercase', letterSpacing:'.5px' },
  table:   { width:'100%', borderCollapse:'collapse' },
  th:      { fontSize:11, color:'#475569', textAlign:'left', padding:'6px 8px',
             borderBottom:'1px solid #1e293b', textTransform:'uppercase' },
  td:      { fontSize:13, color:'#cbd5e1', padding:'8px', borderBottom:'1px solid #1e293b' },
  badge:   (c) => ({ background: c+'20', color: c, padding:'2px 10px',
                     borderRadius:12, fontSize:11, fontWeight:600 }),
  formGrid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 },
  input:   { width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#f1f5f9', borderRadius:8, padding:'9px 11px', fontSize:13, outline:'none' },
  select:  { width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#f1f5f9', borderRadius:8, padding:'9px 11px', fontSize:13 },
  label:   { display:'block', fontSize:12, color:'#94a3b8', marginBottom:6, fontWeight:600 },
  btn:     { background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8, padding:'9px 14px', cursor:'pointer', fontSize:13, fontWeight:700 },
  btn2:    { background:'#1e293b', color:'#cbd5e1', border:'1px solid #334155', borderRadius:8, padding:'8px 12px', cursor:'pointer', fontSize:12, fontWeight:700 },
  ok:      { background:'rgba(34,197,94,.1)', color:'#4ade80', border:'1px solid rgba(34,197,94,.25)', borderRadius:8, padding:'10px 12px', fontSize:13, marginTop:10 },
  err:     { background:'rgba(239,68,68,.1)', color:'#f87171', border:'1px solid rgba(239,68,68,.25)', borderRadius:8, padding:'10px 12px', fontSize:13, marginTop:10 },
};

const COLORS = ['#0ea5e9','#22c55e','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
const STATUS_COLOR = {
  sem_retorno:'#ef4444', em_atendimento:'#f59e0b',
  aguardando_retorno:'#8b5cf6', convertido:'#22c55e', encerrado:'#64748b'
};

function KpiCard({ value, label, sub, color = '#38bdf8' }) {
  return (
    <div style={s.kpi}>
      <div style={{ ...s.kpiVal, color }}>{value ?? '–'}</div>
      <div style={s.kpiLbl}>{label}</div>
      {sub && <div style={s.kpiSub}>{sub}</div>}
    </div>
  );
}

function fmtTma(seg) {
  if (!seg) return '0min';
  const m = Math.floor(seg / 60);
  return m < 60 ? `${m}min` : `${Math.floor(m/60)}h${m%60}min`;
}

function gerarSenha() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const symbols = '@#$%';
  let senha = '';
  for (let i = 0; i < 10; i++) senha += chars[Math.floor(Math.random() * chars.length)];
  return senha + symbols[Math.floor(Math.random() * symbols.length)] + Math.floor(10 + Math.random() * 89);
}

function CriarUsuarioCard() {
  const [form, setForm] = useState({ name:'', email:'', role:'supervisor', password: gerarSenha() });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const criar = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const senhaGerada = form.password;
      const { data } = await api.post('/auth/admins', form);
      setMsg({ type:'ok', text:`Usuario criado: ${data.admin.email}. Senha inicial: ${senhaGerada}` });
      setForm({ name:'', email:'', role:'supervisor', password: gerarSenha() });
    } catch (e) {
      setMsg({ type:'err', text:e.response?.data?.error || 'Erro ao criar usuario' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.card}>
      <div style={s.cardTit}>Gerar usuario do painel</div>
      <div style={s.formGrid}>
        <div>
          <label style={s.label}>Nome</label>
          <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nome do usuario" />
        </div>
        <div>
          <label style={s.label}>E-mail</label>
          <input style={s.input} value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@dominio.com" />
        </div>
        <div>
          <label style={s.label}>Perfil</label>
          <select style={s.select} value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="supervisor">Supervisor</option>
            <option value="superadmin">Superadmin</option>
          </select>
        </div>
        <div>
          <label style={s.label}>Senha inicial</label>
          <div style={{ display:'flex', gap:8 }}>
            <input style={s.input} value={form.password} onChange={e => set('password', e.target.value)} />
            <button type="button" style={s.btn2} onClick={() => set('password', gerarSenha())}>Gerar</button>
          </div>
        </div>
      </div>
      <button style={s.btn} onClick={criar} disabled={saving}>
        {saving ? 'Criando...' : 'Criar usuario'}
      </button>
      {msg && <div style={msg.type === 'ok' ? s.ok : s.err}>{msg.text}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { admin } = useAuth();
  const [periodo, setPeriodo] = useState('hoje');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api.get(`/dashboard/overview?periodo=${periodo}&_=${Date.now()}`)
      .then(r => setData(r.data))
      .catch(() => {
        setData(null);
        setError('Nao foi possivel carregar os dados do dashboard.');
      })
      .finally(() => setLoading(false));
  }, [periodo]);

  const kpis = data?.kpis || {};

  return (
    <div>
      <div style={s.title}>Dashboard</div>
      <div style={s.sub}>Visão geral do atendimento</div>

      <div style={s.filter}>
        {['hoje','7d','30d'].map(p => (
          <button key={p} style={s.fBtn(periodo===p)} onClick={() => setPeriodo(p)}>
            {p === 'hoje' ? 'Hoje' : p === '7d' ? '7 dias' : '30 dias'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color:'#475569', padding:40, textAlign:'center' }}>Carregando...</div>
      ) : error ? (
        <div style={{ ...s.card, color:'#ef4444' }}>{error}</div>
      ) : (
        <>
          {/* KPIs */}
          <div style={s.grid}>
            <KpiCard value={kpis.conversas_total}    label="Conversas totais" />
            <KpiCard value={kpis.conversas_abertas}  label="Conversas abertas"   color="#f59e0b"/>
            <KpiCard value={kpis.mensagens_enviadas} label="Msgs enviadas" />
            <KpiCard value={kpis.contatos_total}     label="Contatos ativos" />
            <KpiCard value={kpis.sem_retorno}        label="Sem retorno"          color="#ef4444"/>
            <KpiCard value={kpis.convertidos}        label="Convertidos"          color="#22c55e"/>
            <KpiCard value={fmtTma(kpis.tma_seg)}   label="TMA (tempo médio)" />
            <KpiCard value={kpis.mensagens_recebidas} label="Msgs recebidas" />
          </div>

          <div style={s.row}>
            {/* Volume por hora */}
            <div style={s.card}>
              <div style={s.cardTit}>Volume de mensagens (24h)</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data?.volumeHora || []}>
                  <XAxis dataKey="hora" tickFormatter={v => v?.substring(11,16)} tick={{fontSize:11,fill:'#475569'}} />
                  <YAxis tick={{fontSize:11,fill:'#475569'}} />
                  <Tooltip contentStyle={{background:'#1e293b',border:'1px solid #334155',color:'#f1f5f9'}}
                           labelFormatter={v => 'Hora: '+v?.substring(11,16)} />
                  <Line type="monotone" dataKey="enviadas"  stroke="#0ea5e9" dot={false} name="Enviadas" />
                  <Line type="monotone" dataKey="recebidas" stroke="#22c55e" dot={false} name="Recebidas" />
                  <Legend wrapperStyle={{fontSize:12,color:'#94a3b8'}} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Status dos contatos */}
            <div style={s.card}>
              <div style={s.cardTit}>Status dos contatos</div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data?.statusContatos || []} dataKey="total" nameKey="status"
                       cx="50%" cy="50%" outerRadius={70} label={e => e.status}>
                    {(data?.statusContatos || []).map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLOR[entry.status] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{background:'#1e293b',border:'1px solid #334155',color:'#f1f5f9'}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Ranking por atendente */}
          {admin?.role === 'superadmin' && <div style={{ marginBottom:20 }}><CriarUsuarioCard /></div>}

          <div style={s.card}>
            <div style={s.cardTit}>Desempenho por atendente</div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Atendente','Conversas','Enviadas','Recebidas','Sem retorno','Convertidos','TMA'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.porAtendente || []).map(a => (
                  <tr key={a.id}>
                    <td style={{ ...s.td, color:'#f1f5f9', fontWeight:600 }}>{a.name}</td>
                    <td style={s.td}>{a.conversas}</td>
                    <td style={s.td}>{a.enviadas}</td>
                    <td style={s.td}>{a.recebidas}</td>
                    <td style={s.td}>
                      <span style={s.badge('#ef4444')}>{a.sem_retorno}</span>
                    </td>
                    <td style={s.td}>
                      <span style={s.badge('#22c55e')}>{a.convertidos}</span>
                    </td>
                    <td style={s.td}>{fmtTma(a.tma_seg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
